import React from 'react';
import styled from 'styled-components';
import getMinimumFractionDigitForFormattedNumber from '@99designs/apps/spa/pages/create/util/getMinimumFractionDigitForFormattedNumber';
import { Box, Divider, Stack } from '@99designs/emu';
import { colors, spaces, typography } from '@99designs/emu/settings';
import { useIsExperimentActivated } from '@99designs/experiments';
import { CurrencyCode } from '@99designs/i18n';
import { Loading } from '@99designs/spa-common';
import { Price } from '@99designs/spa-common';
import { PaymentSummaryFragment } from './PaymentSummary.generated';

const Wrapper = styled.div`
    position: relative;
`;

const LoadingOverlay = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    background-color: ${colors.white};
    opacity: 0.9;

    div {
        animation: none;
    }
`;

const Heading = styled.p`
    font-weight: ${typography.fontWeights.bold};
    font-size: ${typography.typeScale.size5}px;
    font-family: ${typography.fontFamilies.heading};
    margin-bottom: 0;
`;

const Title = styled.p`
    font-weight: ${typography.fontWeights.bold};
    margin-bottom: 0;
    padding-right: ${spaces['0.75bu']};
`;

const OptionTitle = styled.p`
    padding-right: ${spaces['0.75bu']};
    margin-bottom: 0;
    font-size: ${typography.typeScale.size2}px;
    font-weight: ${typography.fontWeights.normal};
    line-height: ${typography.lineHeights.h4};
`;

const DividerWithMargin = styled(Divider)`
    margin: ${spaces['1.5bu']} 0;
`;
interface PaymentSummaryProps {
    taxLabel?: string;
    isLoading?: boolean;
}

const PaymentSummary = ({
    items,
    currency,
    subtotal,
    total,
    bundleSavings,
    tax,
    taxLabel = __('Tax'),
    isLoading,
}: PaymentSummaryProps & Omit<PaymentSummaryFragment, '__typename'>) => {
    const hasLocalizedTax = useIsExperimentActivated('localizedTax', 'enabled');

    return (
        <Wrapper>
            {isLoading && (
                <LoadingOverlay>
                    <Loading />
                </LoadingOverlay>
            )}
            {items.length > 0 && (
                <>
                    <Heading>{__('Summary')}</Heading>
                    <Box marginBottom="1.5bu" />
                    {items.map((item) => (
                        <React.Fragment key={item.title}>
                            <Box display="flex" justifyContent="space-between">
                                <Title>{item.title}</Title>
                                <Price
                                    amount={item.amount}
                                    currency={currency as CurrencyCode}
                                    minimumFractionDigits={0}
                                />
                            </Box>
                            {item.options?.map((option) => (
                                <Box
                                    key={option.title}
                                    display="flex"
                                    justifyContent="space-between"
                                    paddingTop="0.5bu"
                                >
                                    <OptionTitle>{option.title}</OptionTitle>
                                    <Price
                                        amount={option.amount}
                                        currency={currency as CurrencyCode}
                                        minimumFractionDigits={0}
                                    />
                                </Box>
                            ))}
                            <DividerWithMargin />
                        </React.Fragment>
                    ))}
                    <Stack space="1bu">
                        <Box display="flex" justifyContent="space-between">
                            <Title>{__('Subtotal')}</Title>
                            <Price
                                amount={subtotal}
                                currency={currency as CurrencyCode}
                                minimumFractionDigits={0}
                            />
                        </Box>
                        {bundleSavings > 0 && (
                            <Box display="flex" justifyContent="space-between" color="forrestLyf">
                                <Title>{__('Bundle saving')}</Title>
                                <Price
                                    amount={-bundleSavings}
                                    currency={currency as CurrencyCode}
                                    minimumFractionDigits={0}
                                />
                            </Box>
                        )}
                        {hasLocalizedTax && (
                            <Box display="flex" justifyContent="space-between">
                                <span style={{ color: colors.grey500 }}>
                                    {__('Tax calculated at checkout')}
                                </span>
                            </Box>
                        )}
                        {!hasLocalizedTax && tax > 0 && (
                            <Box display="flex" justifyContent="space-between">
                                <Title>{taxLabel}</Title>
                                <Price
                                    amount={tax}
                                    currency={currency as CurrencyCode}
                                    minimumFractionDigits={getMinimumFractionDigitForFormattedNumber(
                                        tax
                                    )}
                                />
                            </Box>
                        )}
                    </Stack>
                    <DividerWithMargin />
                    <Box display="flex" justifyContent="space-between">
                        <Heading>{__('Total')}</Heading>
                        <Heading>
                            <Price
                                amount={total}
                                currency={currency as CurrencyCode}
                                minimumFractionDigits={getMinimumFractionDigitForFormattedNumber(
                                    total
                                )}
                            />
                        </Heading>
                    </Box>
                </>
            )}
        </Wrapper>
    );
};

export default PaymentSummary;
